import { useMemo } from 'react';

import { useFormControls } from 'components/storefront/StorefrontConfigEditor/providers/FormStateProvider';

/**
 * A custom hook that provides handlers for managing a table of sellable products and bundles.
 *
 * @param {Object} slice - The current slice of data containing the items array
 * @param {Array} slice.items - Array of currently selected sellable items and bundles
 * @param {Function} onChange - Callback function to handle changes to the slice data
 * @param {number} index - Index of the slice in the list
 */
function useSellableProductTableHandlers(slice, onChange, index) {
    const { loadingServerData, sellableBundles, sellableItems, uploadProductCSVForSlice } = useFormControls();

    const allSellableProducts = useMemo(
        () => [...(sellableBundles ?? []), ...(sellableItems ?? [])],
        [sellableBundles, sellableItems],
    );

    const options = useMemo(() => {
        const existingProducts = new Set(slice.items.map((item) => item.id));

        return allSellableProducts.reduce((acc, product) => {
            if (!existingProducts.has(product.id)) {
                acc.push({
                    value: product.externalSku,
                    label: `${product.externalSku}: ${product.name}`,
                });
            }
            return acc;
        }, []);
    }, [allSellableProducts, slice.items]);

    const handleAddRow = (selectedExternalSku) => {
        const selectedItem = allSellableProducts.find((item) => item.externalSku === selectedExternalSku);
        if (!selectedItem) return;

        // Skip if item already exists
        if (slice.items.some((item) => item.externalSku === selectedExternalSku)) return;

        onChange({ ...slice, items: [...slice.items, selectedItem] });
    };

    const handleMoveRowToIndex = (externalSku, targetIndex) => {
        const currentIndex = slice.items.findIndex((item) => item.externalSku === externalSku);
        if (currentIndex === -1 || targetIndex < 0 || targetIndex >= slice.items.length) return;

        const newItems = [...slice.items];
        const [movedItem] = newItems.splice(currentIndex, 1);
        newItems.splice(targetIndex, 0, movedItem);

        onChange({ ...slice, items: newItems });
    };

    const moveRowDown = (itemExternalSku) => {
        const itemIndex = slice.items.findIndex((item) => item.externalSku === itemExternalSku);
        if (itemIndex === -1 || itemIndex >= slice.items.length - 1) return;

        const newItems = [...slice.items];
        [newItems[itemIndex], newItems[itemIndex + 1]] = [newItems[itemIndex + 1], newItems[itemIndex]];
        onChange({ ...slice, items: newItems });
    };

    const moveRowUp = (itemExternalSku) => {
        const itemIndex = slice.items.findIndex((item) => item.externalSku === itemExternalSku);
        if (itemIndex <= 0) return;

        const newItems = [...slice.items];
        [newItems[itemIndex - 1], newItems[itemIndex]] = [newItems[itemIndex], newItems[itemIndex - 1]];
        onChange({ ...slice, items: newItems });
    };

    const handleRemoveAllRows = () => {
        onChange({ ...slice, items: [] });
    };

    const handleRemoveRow = (selectedRow) => {
        const newItems = slice.items.filter((item) => item.externalSku !== selectedRow);
        onChange({ ...slice, items: newItems });
    };

    const handleRemoveSelectedRows = (selectedRows) => {
        const newItems = slice.items.filter((item) => !selectedRows.includes(item.externalSku));
        onChange({ ...slice, items: newItems });
    };

    const handleUploadProductCSV = (file) => {
        uploadProductCSVForSlice(file, slice, index);
    };

    return {
        allSellableProducts,
        handleAddRow,
        handleMoveRowToIndex,
        handleRemoveAllRows,
        handleRemoveRow,
        handleRemoveSelectedRows,
        handleUploadProductCSV,
        loading: loadingServerData,
        moveRowDown,
        moveRowUp,
        options,
    };
}

export default useSellableProductTableHandlers;
